<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row>
        <v-col md="12">
          <v-card elevation="3" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Levels</h3>
              <v-chip v-if="allTypesHaveLevels" color="success" text-color="white" small class="ml-2">
                <v-icon left small>mdi-check-circle</v-icon>
                All Level Types Created
              </v-chip>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search ..."
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    clearable
                    @click:clear="(filter.is_active = null), getData()"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading -->
      <v-row v-if="isLoading">
        <v-col md="12">
          <v-card elevation="3" rounded="xxl">
            <v-card-text>
              <v-progress-linear indeterminate color="primary" class="my-4"></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <template v-else>
        <v-row>
          <!-- Wealth Levels -->
          <v-col md="6">
            <v-card elevation="3" rounded="xxl">
              <v-card-title class="amber darken-2 white--text py-3">
                <v-icon color="white" class="mr-2">mdi-currency-usd</v-icon>
                <span class="font-weight-bold">Wealth (Rich) Levels</span>
                <v-spacer></v-spacer>
                <v-chip dark small color="amber darken-4" class="mr-2">{{ wealthLevels.length }} levels</v-chip>
                <v-btn fab x-small color="white" @click="$router.push('/levels/create?type=0')">
                  <v-icon color="amber darken-2">mdi-plus</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-simple-table fixed-header height="600" class="striped-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Level</th>
                        <th class="text-left">Status</th>
                        <th class="text-left">Range</th>
                        <th class="text-left">Coins</th>
                        <th class="text-center">Medal</th>
                      </tr>
                    </thead>
                    <tbody v-if="wealthLevels.length > 0">
                      <tr
                        v-for="(level, i) in wealthLevels"
                        :key="`wealth_${i}`"
                        :class="{
                          'table-row-even': i % 2 === 0,
                          'table-row-odd': i % 2 !== 0,
                        }"
                      >
                        <td>
                          <v-chip small color="amber darken-2" text-color="white">
                            Lv.{{ level.level }}
                          </v-chip>
                        </td>
                        <td>
                          <v-chip
                            small
                            :color="level.is_active == 1 ? 'teal' : 'red'"
                            text-color="white"
                          >
                            {{ level.is_active == 1 ? 'Active' : 'Inactive' }}
                          </v-chip>
                        </td>
                        <td>
                          <span class="font-weight-medium">
                            {{ level.min_levels }} → {{ level.max_levels }}
                          </span>
                        </td>
                        <td>
                          <span class="font-weight-medium">
                            {{ Number(level.coins).toLocaleString() }}
                          </span>
                        </td>
                        <td class="text-center">
                          <template v-if="level.batch_medal">
                            <div v-if="level.batch_medal.medal_path" class="d-flex justify-center mb-1">
                              <v-img
                                :src="`${mediaUrl}${level.batch_medal.medal_path}`"
                                height="36"
                                width="36"
                                contain
                              ></v-img>
                            </div>
                            <v-chip x-small color="amber darken-2" text-color="white">
                              {{ level.batch_medal.label }}
                            </v-chip>
                          </template>
                          <span v-else class="grey--text">-</span>
                        </td>
                      </tr>
                    </tbody>
                    <tbody v-else>
                      <tr>
                        <td colspan="5" class="text-center py-6 grey--text">
                          No wealth levels found
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Charm Levels -->
          <v-col md="6">
            <v-card elevation="3" rounded="xxl">
              <v-card-title class="deep-purple white--text py-3">
                <v-icon color="white" class="mr-2">mdi-star</v-icon>
                <span class="font-weight-bold">Charm Levels</span>
                <v-spacer></v-spacer>
                <v-chip dark small color="deep-purple darken-3" class="mr-2">{{ charmLevels.length }} levels</v-chip>
                <v-btn fab x-small color="white" @click="$router.push('/levels/create?type=1')">
                  <v-icon color="deep-purple">mdi-plus</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-simple-table fixed-header height="600" class="striped-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Level</th>
                        <th class="text-left">Status</th>
                        <th class="text-left">Range</th>
                        <th class="text-left">Coins</th>
                        <th class="text-center">Medal</th>
                      </tr>
                    </thead>
                    <tbody v-if="charmLevels.length > 0">
                      <tr
                        v-for="(level, i) in charmLevels"
                        :key="`charm_${i}`"
                        :class="{
                          'table-row-even': i % 2 === 0,
                          'table-row-odd': i % 2 !== 0,
                        }"
                      >
                        <td>
                          <v-chip small color="deep-purple" text-color="white">
                            Lv.{{ level.level }}
                          </v-chip>
                        </td>
                        <td>
                          <v-chip
                            small
                            :color="level.is_active == 1 ? 'teal' : 'red'"
                            text-color="white"
                          >
                            {{ level.is_active == 1 ? 'Active' : 'Inactive' }}
                          </v-chip>
                        </td>
                        <td>
                          <span class="font-weight-medium">
                            {{ level.min_levels }} → {{ level.max_levels }}
                          </span>
                        </td>
                        <td>
                          <span class="font-weight-medium">
                            {{ Number(level.coins).toLocaleString() }}
                          </span>
                        </td>
                        <td>
                          <div v-if="level.batch_medal" class="d-flex flex-column align-center" style="gap:4px">
                            <v-img
                              v-if="level.batch_medal.medal_path"
                              :src="`${mediaUrl}${level.batch_medal.medal_path}`"
                              height="36"
                              width="36"
                              contain
                            ></v-img>
                            <v-chip x-small color="deep-purple" text-color="white">
                              {{ level.batch_medal.label }}
                            </v-chip>
                          </div>
                          <span v-else class="grey--text">-</span>
                        </td>
                      </tr>
                    </tbody>
                    <tbody v-else>
                      <tr>
                        <td colspan="5" class="text-center py-6 grey--text">
                          No charm levels found
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Delete/Restore Dialogs -->
      <v-dialog v-model="is_deleted_level" width="500">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon large style="color: green; font-size: 90px">mdi-delete-empty</v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <h1 style="color: green">Done!</h1>
                  <br /><h3>Level deleted successfully.</h3><br />
                  <v-btn color="success" @click="is_deleted_level = false">OK</v-btn>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <v-dialog v-model="is_restored_level" width="500">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon large style="color: green; font-size: 90px">mdi-delete-restore</v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <h1 style="color: green">Done!</h1>
                  <br /><h3>Level restored successfully.</h3><br />
                  <v-btn color="success" @click="is_restored_level = false">OK</v-btn>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "LevelsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
      },
      levels: [],
      statuses: [
        { text: "Select Active Status", value: "" },
        { text: "ACTIVE", value: "1" },
        { text: "INACTIVE", value: "0" },
      ],
      page: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      is_deleted_level: false,
      is_restored_level: false,
    };
  },
  computed: {
    wealthLevels() {
      return this.levels.filter((level) => level.type == 0);
    },
    charmLevels() {
      return this.levels.filter((level) => level.type == 1);
    },
    allTypesHaveLevels() {
      return this.wealthLevels.length > 0 && this.charmLevels.length > 0;
    },
  },
  watch: {
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let query =
        "page=1&rowsPerPage=999" +
        "&show_deleted=" + (this.$route.query.open_trash ? 1 : 0) +
        "&is_active=" + (this.filter.is_active ?? "") +
        "&search_keyword=" + (this.filter.search_keyword ?? "");
      await axios
        .get(`levels?${query}`)
        .then((response) => {
          this.levels = response.data.data;
          this.count = response.data.count;
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
  },
};
</script>
